/**
 * Barrier-gate / ANPR entrance-terminal catalog.
 *
 * All-in-one E&E (entrance & exit) machines: a fast boom barrier fused with
 * an ANPR capture camera, LCD and supplement light — the units that sit at
 * parking / site vehicle entrances. The placed instance carries its own boom
 * length + azimuth (Equipment.boomLengthM / boomAzimuthDeg) so the drawn arm
 * matches the chosen pole; the catalog supplies the SKU's boom-length options
 * and the capture/power figures.
 *
 * Every spec below is lifted directly from the manufacturer datasheet; fields
 * the datasheet does not publish are OMITTED rather than guessed.
 *
 * Compiled: 2026-06-10.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const BARRIER_GATE_CATALOG: EquipmentCatalogEntry[] = [
  // SRC: Hikvision datasheet "DS-TMC407-EH(straight boom pole)",
  //      DS-TMC407-EHstraight-boom-pole_Datasheet_20260424.
  //      All-in-one E&E machine: gear barrier gate + 4 MP ANPR capture unit
  //      + 21.5" LCD + IR/warm-white supplement light + anti-fall radar +
  //      help button + two-way audio. 3 m / 4 m straight octagonal pole.
  {
    id: 'hikvision-ds-tmc407-eh',
    manufacturer: 'Hikvision',
    model: 'DS-TMC407-EH',
    category: 'barrier-gate',
    tagline: 'All-in-one E&E barrier — 4 MP ANPR capture, straight 3/4 m boom + 21.5″ LCD',
    verified: true,
    specs: {
      boomLengthOptionsM: [3, 4],
      boomType: 'straight',
      risingTimeS: 0.9,   // 3 m pole, default (adjustable 0.9 / 1.2 / 2 s)
      fallingTimeS: 3,    // 3 m pole, default (adjustable 1.5 / 2 / 3 s)
      boomDirection: 'left-or-right',
      runningNoiseDb: 65,
      lifespanCycles: 5_000_000,

      cameraMP: 4,
      cameraResolution: '2688 × 1520',
      lens: '3.1–6 mm motor-driven varifocal',
      minLux: 0.022,      // colour @ F2.0, AGC on (B/W 0.011)
      anpr: true,
      recognition: ['ANPR (plate)', 'vehicle type', 'vehicle logo', 'sub-brand'],

      lcdInches: 21.5,    // 1080p, 1500 cd/m²
      supplementLight: 'IR + warm white, 9 LED (3000 K)',

      powerConsumptionW: 350,
      powerSupply: '220 V, 50 Hz',
      ipRating: 'IP54',
      operatingTempC: '-30 °C to 70 °C',
      weightKg: 95,

      networkPorts: 2,    // 2× RJ45 10/100
      alarmInputs: 3,
      alarmOutputs: 2,    // relay outputs (boom rise/fall)
    },
  },
];
